import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { trpc } from '@web/lib/trpc';
import { z } from 'zod';
import { Form, FormField, FormItem, FormLabel } from '@web/components/ui/form';
import { Input } from '@web/components/ui/input';
import { Card, CardContent } from '@web/components/ui/card';
import { Button } from '@web/components/ui/button';
import { env } from '@web/utils';

async function create({ authToken }: { authToken: string }) {
  const myHeaders = new Headers();
  myHeaders.append('Content-Type', 'application/x-www-form-urlencoded');

  const urlencoded = new URLSearchParams();
  urlencoded.append('tokenizationKey', authToken);

  const requestOptions = {
    method: 'POST',
    headers: myHeaders,
    body: urlencoded,
  };

  const res = await fetch(
    'https://secure.nmi.com/token/api/create',
    requestOptions,
  ).then((response) => response.text());

  const token = z.object({ token: z.string() }).safeParse(JSON.parse(res))
    .data?.token;

  return token;
}

async function register({
  authToken,
  paymentToken,
  cardNumber,
  exp,
  cvv,
}: {
  authToken: string;
  paymentToken: string;
  cardNumber: string;
  exp: string;
  cvv: string;
}) {
  const myHeaders = new Headers();
  myHeaders.append('Content-Type', 'application/json');

  const raw = JSON.stringify({
    tokenizationKey: authToken,
    cartCorrelationId: '',
    tokenId: paymentToken,
    data: [
      {
        elementId: 'ccnumber',
        value: cardNumber,
      },
      {
        elementId: 'ccexp',
        value: exp,
      },
      {
        elementId: 'cvv',
        cvvDisplay: false,
        value: cvv,
      },
    ],
  });

  const requestOptions = {
    method: 'POST',
    headers: myHeaders,
    body: raw,
  };

  const res = await fetch(
    'https://secure.nmi.com/token/api/save_multipart_token',
    requestOptions,
  ).then((response) => response.text());

  const obj = JSON.parse(res) as unknown;

  return obj;
}

const paymentFormSchema = z.object({
  number: z.string().min(1),
  exp: z.string().min(1),
  cvv: z.string().min(1),
  amount: z.string().min(1),
});

export function PaymentView() {
  const { mutateAsync: initiatePaymentAsync } =
    trpc.payment.initiatePayment.useMutation();

  const form = useForm<z.infer<typeof paymentFormSchema>>({
    resolver: zodResolver(paymentFormSchema),
    defaultValues: {
      number: '4111111111111111',
      exp: '1234',
      cvv: '123',
      amount: '100',
    },
  });

  const handleSubmit = async (values: z.infer<typeof paymentFormSchema>) => {
    console.log(values);

    const authToken = env.VITE_NMI_PUBLIC_KEY;
    if (!authToken) return;

    const paymentToken = await create({ authToken });
    console.log('-->   ~ handleSubmit ~ paymentToken:', paymentToken);
    if (!paymentToken) return;

    const registration = await register({
      authToken,
      paymentToken,
      cardNumber: values.number,
      exp: values.exp,
      cvv: values.cvv,
    });
    console.log('-->   ~ handleSubmit ~ registration:', registration);

    await initiatePaymentAsync({ paymentToken, amount: values.amount });
  };

  return (
    <div className="flex h-screen items-center justify-center">
      <Card className="w-96">
        <CardContent>
          <Form {...form}>
            <form
              onSubmit={(e) => {
                void form.handleSubmit(handleSubmit)(e);
              }}
              className="mt-6 space-y-5"
            >
              <FormField
                control={form.control}
                name="number"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormLabel>Card Number</FormLabel>
                    <Input {...field} />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="exp"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormLabel>Exp</FormLabel>
                    <Input {...field} />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="cvv"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormLabel>CVV</FormLabel>
                    <Input {...field} />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="amount"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormLabel>Amount</FormLabel>
                    <Input {...field} />
                  </FormItem>
                )}
              />

              <Button type="submit" className="">
                Submit
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
